import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Youtube, Facebook, Instagram } from 'lucide-react';

const FooterContainer = styled.footer`
    background-color: ${({ theme }) => theme.colors.backgroundDarker};
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};
    margin-top: auto;
    position: relative;
    z-index: 20;
`;

const FooterContent = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xl};
`;

const NavLinks = styled.nav`
    display: flex;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.xl};
    flex-wrap: wrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        gap: ${({ theme }) => theme.spacing.md};
    }
`;

const NavLink = styled(Link)`
    text-decoration: none;
    color: ${({ theme }) => theme.colors.text};
    font-size: ${({ theme }) => theme.fontSizes.small};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 0;

    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const SocialLinks = styled.div`
    display: flex;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.lg};
    align-items: center;
`;

const SocialLink = styled.a`
    color: ${({ theme }) => theme.colors.text};
    transition:
        color 0.2s ease,
        transform 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        color: ${({ theme }) => theme.colors.primary};
        transform: scale(1.1);
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

const LegalText = styled.p`
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: ${({ theme }) => theme.fontSizes.small};
    line-height: 1.6;
    text-align: center;
    max-width: 900px;
    margin: 0 auto;
`;

export const Footer: React.FC = () => {
    return (
        <FooterContainer>
            <FooterContent>
                <NavLinks>
                    <NavLink to="/">HOME</NavLink>
                    <NavLink to="/albums">ALBUMS</NavLink>
                    <NavLink to="/videos">VIDEOS</NavLink>
                    <NavLink to="/gallery">GALLERY</NavLink>
                    <NavLink to="/shows">SHOWS</NavLink>
                    <NavLink to="/story">STORY</NavLink>
                    <NavLink to="/contact">CONTACT</NavLink>
                </NavLinks>

                <SocialLinks>
                    <SocialLink
                        href="https://www.instagram.com/the.butterscotch.boys/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                    >
                        <Instagram />
                    </SocialLink>

                    <SocialLink
                        href="https://www.youtube.com/@TheButterscotchBoys"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                    >
                        <Youtube />
                    </SocialLink>
                    <SocialLink
                        href="https://www.facebook.com/profile.php?id=61567843858058"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                    >
                        <Facebook />
                    </SocialLink>
                </SocialLinks>

                <LegalText>
                    The Butterscotch Boys is the band name under which Tyler Vander Maas and his
                    musical collaborators provide entertainment services. The Butterscotch Boys and
                    its members practice in accordance with applicable laws, regulations, and
                    professional standards for musical performance. Individual band members are
                    independently contracted and are not liable for the services provided by any
                    other member performing under The Butterscotch Boys brand. Our use of the terms
                    "our band" and "we" and "us" and terms of similar import, denote the
                    collaborative structure conducted by The Butterscotch Boys and its individual
                    members.
                </LegalText>
            </FooterContent>
        </FooterContainer>
    );
};
